/**
 * TrackArtwork Component
 * Displays track artwork with fallback to uploader's profile picture
 * Features: Lazy loading, caching, optimized image loading
 */
import { useState, useEffect, useRef, memo } from 'react';
import { Music } from 'lucide-react';
import { getProfilePicUrl } from '../utils/profilePic';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Simple in-memory cache for uploader pictures
const uploaderPictureCache = new Map();

export const TrackArtwork = memo(({ 
  track, 
  className = "w-full h-full object-cover",
  fallbackIconSize = 24,
  showFallbackIcon = true 
}) => {
  const [imageError, setImageError] = useState(false);
  const [uploaderPicture, setUploaderPicture] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const imgRef = useRef(null);

  // Reset states when track changes
  useEffect(() => {
    setImageError(false);
    setIsLoaded(false);
  }, [track?.id]);

  // Check cache and fetch uploader's profile picture if no artwork
  useEffect(() => {
    const fetchUploaderPicture = async () => {
      if (!track?.artwork && track?.user_id) {
        // Check cache first
        if (uploaderPictureCache.has(track.user_id)) {
          setUploaderPicture(uploaderPictureCache.get(track.user_id));
          return;
        }
        
        try {
          const response = await fetch(`${BACKEND_URL}/api/profile/${track.user_id}`);
          if (response.ok) {
            const data = await response.json();
            if (data.profile_picture) {
              uploaderPictureCache.set(track.user_id, data.profile_picture);
              setUploaderPicture(data.profile_picture);
            }
          }
        } catch (error) {
          // Silent fail - will show fallback
        }
      }
    };

    fetchUploaderPicture();
  }, [track?.user_id, track?.artwork]);

  const handleLoad = () => setIsLoaded(true);
  const handleError = () => setImageError(true);

  // If track has artwork, show it
  if (track?.artwork && !imageError) {
    // Handle both full URLs and filenames
    const artworkUrl = track.artwork.startsWith('http') 
      ? track.artwork 
      : `${BACKEND_URL}/api/artwork/${track.artwork}`;
    
    return (
      <div className="relative w-full h-full bg-[#111]">
        {!isLoaded && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-5 h-5 border-2 border-[#333] border-t-[#00CCFF] rounded-full animate-spin" />
          </div>
        )}
        <img 
          ref={imgRef}
          src={artworkUrl} 
          alt={track.title || 'Track artwork'} 
          className={`${className} transition-opacity duration-200 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
          loading="lazy"
          decoding="async"
          onLoad={handleLoad}
          onError={handleError}
        />
      </div>
    );
  }

  // Fallback to uploader's profile picture
  if (uploaderPicture) {
    const pictureUrl = uploaderPicture.startsWith('http') 
      ? uploaderPicture 
      : getProfilePicUrl(uploaderPicture, track?.user_id);
    
    return (
      <div className="relative w-full h-full bg-[#111]">
        {!isLoaded && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-5 h-5 border-2 border-[#333] border-t-[#00CCFF] rounded-full animate-spin" />
          </div>
        )}
        <img 
          src={pictureUrl} 
          alt={track?.artist || 'Artist'} 
          className={`${className} transition-opacity duration-200 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
          loading="lazy"
          decoding="async"
          onLoad={handleLoad}
          onError={() => setUploaderPicture(null)}
        />
      </div>
    );
  }

  // Final fallback - show icon
  if (showFallbackIcon) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a]">
        <Music size={fallbackIconSize} className="text-[#333]" />
      </div>
    );
  }

  return null;
});

TrackArtwork.displayName = 'TrackArtwork';

/**
 * Helper function to get artwork URL with fallback.
 * Use this when you need just the URL, not the component.
 *
 * @param {object} track - the track doc (uses track.artwork + track.user_id)
 * @param {string|null} uploaderProfilePicture - optional uploader picture
 *   filename/URL. When not a full URL we fall back to
 *   `/api/users/{track.user_id}/profile-picture`.
 */
export const getArtworkUrl = (track, uploaderProfilePicture = null) => {
  if (track?.artwork) {
    return track.artwork.startsWith('http') 
      ? track.artwork 
      : `${BACKEND_URL}/api/artwork/${track.artwork}`;
  }
  if (uploaderProfilePicture) {
    if (uploaderProfilePicture.startsWith('http')) return uploaderProfilePicture;
    return getProfilePicUrl(uploaderProfilePicture, track?.user_id);
  }
  return null;
};

export default TrackArtwork;
